import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { PrintService } from '../../services/print.service';
import { Print } from '../../models/Print';
import { Orientation } from '../../models/Orientation';
import { Region } from '../../models/Region';
import { Style } from '../../models/Style';
import { Size } from '../../models/Size';
import { UnitOfLengthAbbrPipe } from '../../pipes/unit-of-length-abbr.pipe';
import { MapifyPricePipe } from '../../pipes/mapify-price.pipe';

@Component({
  selector: 'mpf-map-config-size',
  templateUrl: './map-config-size.component.html',
  styleUrls: ['./map-config-size.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapConfigSizeComponent implements OnInit {
  @Input() style: Style;
  @Input() printId: string;

  private _size: Size;

  @Input()
  set size(size: Size) {
    this._size = size;
    this.sizeChange.emit(size);
  }

  get size() {
    return this._size;
  }

  @Output() sizeChange = new EventEmitter<Size>();
  @Output() printChange = new EventEmitter<Print>();

  orientations: Orientation[];
  orientation: Orientation;
  regions: Region[];
  region: Region;
  prints: Print[];
  print: Print;

  constructor(
    private printService: PrintService,
    private mapifyPricePipe: MapifyPricePipe,
    private unitOfLengthAbbrPipe: UnitOfLengthAbbrPipe
  ) {}

  ngOnInit() {
    const { orientations, regions } = environment.mapify;

    this.orientations = <Orientation[]>orientations;
    // the initial orientation is the orientation defined on the current size
    this.orientation = this.size.orientation;
    this.regions = <Region[]>regions;

    // when a map is restored, the region determined by the size unit has
    // precedence over the default region
    const sizeRegion = this.regions.find(r => r.unit === this.size.unit);

    const defaultRegion: Region = {
      name: 'EU',
      unit: 'cm'
    };

    this.region = sizeRegion !== undefined ? sizeRegion : defaultRegion;

    this.updatePrints();
  }

  updatePrints() {
    // filter prints according to the current region, orientation and style
    this.prints = this.printService.listBy(this.region, this.orientation, this.style);
    const print = this.prints.find(p => p.id === this.printId) || this.prints[0];

    this.selectPrint(print);
  }

  selectOrientation(orientation: Orientation) {
    this.orientation = orientation;

    this.updatePrints();
  }

  isOrientationSelected(orientation: Orientation) {
    return this.orientation === orientation;
  }

  selectRegion(region: Region) {
    this.region = region;

    this.updatePrints();
  }

  isRegionSelected(region: Region) {
    return this.region.name === region.name;
  }

  selectPrint(print: Print) {
    // when a print is selected we have to notify that because some other components like the
    // map-configurator have to update for example the 'price'
    this.print = print;
    this.printChange.emit(this.print);

    // a print can have multiple sizes, due to multiple orientations. So we have to get the size with
    // the current orientation and update it
    const size = this.getSizeWithCurrentOrientation(print.sizes);

    if (size) {
      this.size = size;
    }
  }

  isPrintSelected(print: Print) {
    const size = this.getSizeWithCurrentOrientation(print.sizes);

    if (size) {
      return this.compareSize(size, this.size);
    }
  }

  getSizeWithCurrentOrientation(sizes: Size[]): Size | void {
    return sizes.find(s => s.orientation === this.orientation);
  }

  compareSize(sizeA: Size, sizeB: Size) {
    return sizeA.width === sizeB.width && sizeA.height === sizeB.height;
  }

  getPrintPrice(print: Print): string {
    return this.mapifyPricePipe.transform([print.id]);
  }

  getPrintSizeText(print: Print): string | void {
    const size = this.getSizeWithCurrentOrientation(print.sizes);

    if (size) {
      return `${size.width} x ${size.height} ${this.unitOfLengthAbbrPipe.transform(size.unit)}`;
    }
  }
}



// WEBPACK FOOTER //
// /vercel/path0/src/app/components/map-config-size/map-config-size.component.ts